import { InfoOutlineIcon } from '@chakra-ui/icons';
import { Box, Flex, Popover, PopoverContent, PopoverTrigger, SkeletonText, Tab, TabList, TabPanel, TabPanels, Tabs, Text, useColorModeValue, useToast } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { SlGraph } from 'react-icons/sl';
import { useGetAudienceEstimateListMutation } from 'store/campaigns/adsetFormApi';
import { removeEmptyValues } from 'utils/functions';

function Audience({ adsAccount, targeting_spec, context, promoted_object, optimizationGoal }) {

    const toast = useToast();
    const [selectedTab, setSelectedTab] = useState('overview');
    const backgroundColor = useColorModeValue('gray.50', 'gray.800');

    const geoLocationList = targeting_spec?.geo_locations || null;

    const [getAudienceEstimate, { data: audienceList, isLoading, error }] = useGetAudienceEstimateListMutation();


    useEffect(() => {
        if (error) {
            toast({
                position: "top-right",
                title: "Error",
                description: error?.data?.message,
                status: "error",
                duration: 4000,
                isClosable: true,
            });
        }
    }, [error]);

    useEffect(() => {
        if (geoLocationList) {

            let finalPromotedObject = undefined;

            if (context == 'object') {
                finalPromotedObject = promoted_object;
            } else {
                if (Array.isArray(promoted_object) && promoted_object.length > 0) {
                    const { pixelName, adsAccountId, ...rest } = promoted_object[0];
                    finalPromotedObject = rest;
                }
            }

            const payload = {
                targeting_spec: targeting_spec
            }
            const filteredPayload = removeEmptyValues(payload);

            getAudienceEstimate({
                adsAccountId: adsAccount,
                payload: {
                    promoted_object: finalPromotedObject,
                    optimization_goal: optimizationGoal,
                    ...filteredPayload
                }
            });
        }
    }, [targeting_spec]);


    return (
        <Box>
            <Flex borderRadius="md" bg={backgroundColor} py={'10px'} px={'20px'} direction={'column'}>
                <Flex align="center">
                    <Text fontWeight="bold">Audience definition</Text>
                    <Popover placement="bottom" closeOnBlur={true}>
                        <PopoverTrigger>
                            <InfoOutlineIcon pointerEvents="all" ml={2} />
                        </PopoverTrigger>
                        <PopoverContent>
                            <Box fontSize="sm" p={'10px'} bg={'gray.700'}>
                                <Text color={'#fff'}>
                                    <strong>Audience definition</strong> <br />
                                    The audience definition scale indicates whether an audience is specific or broad based on the audience selections that you added. When an audience is too specific, ad delivery may be affected.
                                </Text>
                            </Box>
                        </PopoverContent>
                    </Popover>
                </Flex>

                {isLoading ? (
                    <Flex h={'9vh'} mt={4}>
                        <SkeletonText width="80%" noOfLines={3} skeletonHeight={2} />
                    </Flex>
                ) : error ? (
                    <Box mt={4}>
                        <Text fontSize={'12px'}> There was no audience estimate yet.</Text>
                    </Box>
                ) : (
                    <Box mt={4}>
                        {audienceList?.data?.[0]?.estimate_ready ? (
                            <Flex direction={'column'} gap={2}>
                                <Flex align="center">
                                    <Text fontSize={'11px'} letterSpacing={-0.5}>
                                        Estimated audience size: {audienceList.data[0].estimate_mau_lower_bound.toLocaleString()} - {audienceList.data[0].estimate_mau_upper_bound.toLocaleString()}
                                    </Text>
                                    <Popover placement="bottom-end" closeOnBlur={true}>
                                        <PopoverTrigger>
                                            <InfoOutlineIcon pointerEvents="all" ml={2} />
                                        </PopoverTrigger>
                                        <PopoverContent>
                                            <Box fontSize="sm" p={'5px'} bg={'gray.700'}>
                                                <Tabs size="sm" isLazy onChange={(index) => setSelectedTab(index === 0 ? 'overview' : 'details')} color={'#fff'} colorScheme={'brand'}>
                                                    <Text margin={2} fontWeight={'bold'}>How this number is estimated</Text>
                                                    <TabList>
                                                        <Tab>Overview</Tab>
                                                        <Tab>Details</Tab>
                                                    </TabList>
                                                    <TabPanels>
                                                        <TabPanel>
                                                            {selectedTab === 'overview' && (
                                                                <Text color={'#fff'}>
                                                                    Estimated audience size is an estimate of how many Accounts Centre accounts may meet your targeting criteria. It is based on factors such as targeting selections, ad placements and how many Accounts Centre accounts were shown ads on Meta apps and services in the past 30 days.<br></br>
                                                                    This is not an estimate of how many people will see your ad and is not designed to match population or census data. This metric is in in development.
                                                                </Text>
                                                            )}
                                                        </TabPanel>
                                                        <TabPanel>
                                                            {selectedTab === 'details' && (
                                                                <Text color={'#fff'} lineHeight="1.4">
                                                                    This estimate is affected by the ad placements you've selected:<br />
                                                                    <strong>Facebook:</strong> Feed, Profile feed, In-stream videos, Right column, Video feeds, Marketplace, Stories, Overlay ads in Reels, Search results and Reels <br />
                                                                    <strong>Instagram:</strong> Feed, Stories, Explore, Explore home, Reels, Profile feed and Search results <br />
                                                                    <strong>Messenger:</strong>Messenger inbox and Stories <br />
                                                                    <strong>Audience Network:</strong> Native, banner and interstitial and Rewarded videos <br />
                                                                </Text>
                                                            )}
                                                        </TabPanel>
                                                    </TabPanels>
                                                </Tabs>
                                            </Box>
                                        </PopoverContent>
                                    </Popover>
                                </Flex>
                                <Flex gap={2}>
                                    <SlGraph fontSize="30px" />
                                    <Text fontSize={'11px'}>
                                        Estimates may vary significantly over time based on your targeting selections and available data and do not reflect Advantage audience options.
                                    </Text>
                                </Flex>
                            </Flex>
                        ) : (
                            <Text fontSize={'12px'}>No audience estimatation available yet.</Text>
                        )}
                    </Box>
                )}
            </Flex>
        </Box>
    );
}

export default Audience;
